import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { NAV_ITEMS } from "@/lib/constants";

export default function HubLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV_ITEMS.filter((n) => n.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-greige flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-black/10 bg-greige fixed inset-y-0 left-0 z-40">
        <div className="px-8 pt-10 pb-8">
          <span className="font-display text-2xl tracking-tight text-ink block">twoe</span>
          <span className="meta-label">CLOUD HUB</span>
        </div>
        <nav className="flex-1 px-8 space-y-5 overflow-y-auto">
          {items.map((item) => {
            const active = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}-link`}
                className="flex items-baseline gap-3 group"
              >
                <span className="meta-label">{item.meta}</span>
                <span className={`link-underline font-body text-sm tracking-wide transition-colors ${active ? "text-ink font-medium" : "text-stone2e group-hover:text-ink"}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
        <div className="px-8 py-8 border-t border-black/10">
          <p className="font-body text-sm text-ink truncate">{user?.name}</p>
          <p className="meta-label mt-0.5">{user?.role === "lavoratore" ? "LAVORATORE" : "CLIENTE IN VISIONE"}</p>
          <button onClick={handleLogout} data-testid="logout-button" className="mt-4 flex items-center gap-2 text-stone2e hover:text-ink transition-colors text-sm">
            <LogOut strokeWidth={1.5} size={16} /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-greige/80 backdrop-blur-xl border-b border-black/10">
        <div className="flex items-center justify-between px-5 h-16">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-xl text-ink">twoe</span>
            <span className="meta-label">CLOUD HUB</span>
          </div>
          <button onClick={() => setOpen(true)} data-testid="menu-open-button" aria-label="Apri menu" className="text-ink">
            <Menu strokeWidth={1.5} size={24} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-greige flex flex-col p-8 overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-12">
              <span className="font-display text-2xl">twoe cloud hub</span>
              <button onClick={() => setOpen(false)} data-testid="menu-close-button" aria-label="Chiudi menu">
                <X strokeWidth={1.5} size={26} />
              </button>
            </div>
            <nav className="flex flex-col gap-5">
              {items.map((item, i) => (
                <motion.div key={item.path} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
                  <Link
                    to={item.path}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.path.slice(1)}-link`}
                    className="flex items-baseline gap-4"
                  >
                    <span className="meta-label">{item.meta}</span>
                    <span className="font-display text-2xl text-ink">{item.label}</span>
                  </Link>
                </motion.div>
              ))}
            </nav>
            <button onClick={handleLogout} className="mt-auto pt-10 flex items-center gap-3 text-stone2e" data-testid="mobile-logout-button">
              <LogOut strokeWidth={1.5} size={20} /> <span className="font-body">Esci</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-w-0">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="px-5 md:px-12 py-10 md:py-14 max-w-6xl"
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}
